'use client';

import { cn } from '@/lib/utils';
import { a, useSpring } from '@react-spring/web';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

const valuesEN = [
  {
    id: 1,
    title: 'Customer centricity',
    description:
      'Prioritizing a deep understanding  of customer behaviors and needs, allowing brands to tailor experiences that resonate with their audiences, both in-store and online.',
  },
  {
    id: 2,
    title: 'Innovation',
    description:
      "Embracing the latest advancements, and pushing the boundaries of what's possible in order to deliver groundbreaking solutions that redefine creativity and drive industry transformation.",
  },
  {
    id: 3,
    title: 'Aesthetic sensibility',
    description:
      'Prioritizing beauty and elegance in design, crafting  visually striking that artistically refined solutions that harmoniously blend form and function, evoking emotion in every detail.',
  },
  {
    id: 4,
    title: 'Visionary leadership',
    description:
      'Leading the way in both design and technology, setting new standards and guiding the future direction of the industry with a clear, forward-thinking vision.',
  },
];
const valuesIT = [
  {
    id: 1,
    title: 'Customer centricity',
    description:
      'Diamo priorità a una profonda comprensione dei comportamenti e delle esigenze dei clienti, permettendo ai brand di creare esperienze su misura che risuonano con il loro pubblico, sia online che nei punti vendita.',
  },
  {
    id: 2,
    title: 'Innovazione',
    description:
      'Abbracciamo le tecnologie più avanzate e spingiamo i confini di ciò che è possibile per offrire soluzioni rivoluzionarie, ridefinendo la creatività e guidando la trasformazione del settore.',
  },
  {
    id: 3,
    title: 'Sensibilità estetica',
    description:
      'Poniamo al centro bellezza ed eleganza nel design, creando soluzioni visivamente sorprendenti e artisticamente raffinate, che fondono armoniosamente forma e funzione, suscitando emozioni in ogni dettaglio.',
  },
  {
    id: 4,
    title: 'Leadership visionaria',
    description:
      'Guidiamo l’evoluzione del design e della tecnologia, fissando nuovi standard e tracciando il futuro del settore con una visione chiara e innovativa.',
  },
];

export default function ValuesBlock({ lang }: { lang: string }) {
  const [y, setY] = useState(0);

  const circleStyle = useSpring({
    y: y,
    config: { tension: 280, friction: 60 },
  });
  const values = lang === 'en' ? valuesEN : valuesIT;

  return (
    <section className=' pt-32 pb-72 sm:px-8 sm:pt-20 sm:pb-40 px-28'>
      <p className='uppercase 2xl:text-[30px] text-[20px] leading-none font-bold'>
        our values
      </p>
      <div className='relative grid grid-cols-1 gap-40 sm:pl-24 sm:gap-20 mt-20'>
        <div className='absolute top-0 left-0 sm:mx-0 sm:left-8 right-0 mx-auto h-[calc(100%+200px)] sm:h-[calc(100%+100px)] w-[2px] bg-[#FDFDFDCC]' />
        <a.div
          style={circleStyle}
          className='absolute left-0 right-0 sm:mx-0 sm:left-5 mx-auto w-6 h-6 rounded-full bg-white transition-all duration-500'
          // style={{ top: `${150 + activeIndex * 200}px` }} // 200px = высота одного блока (подгони под себя)
        />
        {values.map((i, index) => (
          <ValueBlock key={i.id} data={i} setY={setY} />
        ))}
      </div>
    </section>
  );
}

function ValueBlock({
  data,
  setY,
}: {
  data: {
    title: string;
    description: string;
  };
  setY: Dispatch<SetStateAction<number>>;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(false);

  const handleScroll = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const middle = rect.top + rect.height / 2;
      const windowMiddle = window.innerHeight / 2;
      const isActive = Math.abs(middle - windowMiddle) < 100;

      if (isActive) {
        setActive(true);
        const middleY = ref.current.offsetTop + rect.height / 2;
        setY(middleY);
      } else {
        setActive(false);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const blockStyle = useSpring({
    color: active ? '#FFFFFF' : '#616161',
  });

  return (
    <a.div
      ref={ref}
      style={blockStyle}
      className={cn('grid grid-cols-2 sm:grid-cols-1 sm:gap-4')}
    >
      <p className='2xl:text-[55px] text-[36px] font-normal sm:text-[20px] '>
        {data.title}
      </p>
      <p className='font-poppins font-normal 2xl:text-[20px] text-[13px] pl-32 sm:pl-0 sm:text-[12px] sm:leading-[120%] sm:tracking-[-0.05em] mt-3 leading-[120%]'>
        {data.description}
      </p>
    </a.div>
  );
}
